/* global BigInt */
//Reads and formats the public header block of a LAS point-cloud file, mirroring the packed
//Header struct in GeoFuseUtility Io/Lasv1p4IO.h (LAS 1.4, little-endian, 375 bytes; the
//1.0-1.3 base block is the first 227). Field names follow that struct / its printHeader().
//buildLasReport additionally verifies the file size the header implies: pointDataOffset
//(header + VLRs) + numPoints x pointDataRecordLen must equal the size on disk -- exact
//unless the file carries waveform packets or extended VLRs, whose sizes the fixed header
//does not state.

export const LAS_HEADER_BYTES = 375;
const BASE_HEADER_BYTES = 227; //through min.z, the pre-1.3 header

const asciiZ = (bytes, start, len) => {
    let out = '';

    for (let i = start; i < start + len; i++)
    {
        if (0 === bytes[i]) break;
        out += String.fromCharCode(bytes[i]);
    }

    return out;
};

const hexBytes = (bytes, start, len) => {
    const parts = [];

    for (let i = start; i < start + len; i++)
    {
        parts.push(bytes[i].toString(16).padStart(2, '0'));
    }

    return parts.join(' ');
};

//ArrayBuffer (>= the base header) -> one 'name: value' line per struct field. Throws with a
//readable message when the buffer is not a LAS header.
export const parseLasHeader = (buffer) => {

    if (buffer.byteLength < BASE_HEADER_BYTES)
    {
        throw new Error('file is too small to hold a LAS header (' + buffer.byteLength + ' bytes)');
    }

    const view = new DataView(buffer);
    const bytes = new Uint8Array(buffer);
    const signature = asciiZ(bytes, 0, 4);

    if ('LASF' !== signature)
    {
        throw new Error("file does not start with the LASF signature");
    }

    const f64x3 = (offset) => [view.getFloat64(offset, true), view.getFloat64(offset + 8, true), view.getFloat64(offset + 16, true)].join(', ');

    const lines = [];

    lines.push('fileSignature: ' + signature);
    lines.push('fileSourceID: ' + view.getUint16(4, true));
    lines.push('globalEncoding: ' + view.getUint16(6, true));
    lines.push('guidData1: ' + view.getUint32(8, true));
    lines.push('guidData2: ' + view.getUint16(12, true));
    lines.push('guidData3: ' + view.getUint16(14, true));
    lines.push('guidData4: ' + hexBytes(bytes, 16, 8));
    lines.push('versionMajor: ' + view.getUint8(24));
    lines.push('versionMinor: ' + view.getUint8(25));
    lines.push('systemIdentifier: ' + asciiZ(bytes, 26, 32));
    lines.push('generatingSoftware: ' + asciiZ(bytes, 58, 32));
    lines.push('creationDay: ' + view.getUint16(90, true));
    lines.push('creationYear: ' + view.getUint16(92, true));
    lines.push('headerSize: ' + view.getUint16(94, true));
    lines.push('pointDataOffset: ' + view.getUint32(96, true));
    lines.push('numVarLenRecords: ' + view.getUint32(100, true));
    lines.push('pointDataFormat: ' + view.getUint8(104));
    lines.push('pointDataRecordLen: ' + view.getUint16(105, true));
    lines.push('numPointsLegacy: ' + view.getUint32(107, true));

    const legacyByReturn = [];
    for (let i = 0; i < 5; i++)
    {
        legacyByReturn.push(view.getUint32(111 + i * 4, true));
    }
    lines.push('numPointsByReturnLegacy: ' + legacyByReturn.join(', '));

    lines.push('scale.xyz: ' + f64x3(131));
    lines.push('offset.xyz: ' + f64x3(155));
    //the struct stores max/min interleaved per axis; present them grouped like printHeader()
    lines.push('max.xyz: ' + [view.getFloat64(179, true), view.getFloat64(195, true), view.getFloat64(211, true)].join(', '));
    lines.push('min.xyz: ' + [view.getFloat64(187, true), view.getFloat64(203, true), view.getFloat64(219, true)].join(', '));

    //fields past the 1.0-1.2 base block exist only when the header is big enough for them
    const headerSize = view.getUint16(94, true);

    if (headerSize >= BASE_HEADER_BYTES + 8 && buffer.byteLength >= BASE_HEADER_BYTES + 8)
    {
        lines.push('startWaveformDataPacketRecord: ' + view.getBigUint64(227, true).toString());
    }

    if (headerSize >= LAS_HEADER_BYTES && buffer.byteLength >= LAS_HEADER_BYTES)
    {
        lines.push('startExtendedVariableLengthRecord: ' + view.getBigUint64(235, true).toString());
        lines.push('numberExtendedVariableLengthRecord: ' + view.getUint32(243, true));
        lines.push('numPoints: ' + view.getBigUint64(247, true).toString());

        const byReturn = [];
        for (let i = 0; i < 15; i++)
        {
            byReturn.push(view.getBigUint64(255 + i * 8, true).toString());
        }
        lines.push('numPointsByReturn: ' + byReturn.join(', '));
    }

    return lines.join('\n');
};

//Header text plus the size check for a las of actualSize bytes on disk.
//Returns { text, ok } -- ok drives the popup's tick/cross.
export const buildLasReport = (buffer, actualSize) => {

    let text;

    try
    {
        text = parseLasHeader(buffer);
    }
    catch (err)
    {
        return { text: 'Could not read LAS header: ' + err.message, ok: false };
    }

    const view = new DataView(buffer);
    const headerSize = view.getUint16(94, true);
    const pointDataOffset = view.getUint32(96, true);
    const recordLen = view.getUint16(105, true);

    //1.4 carries the point count as a u64 past the base block; earlier versions (or a 1.4
    //writer that only filled the legacy field) use the u32
    let numPoints = BigInt(view.getUint32(107, true));
    let hasWaveform = false;
    let numEVLR = 0;

    if (headerSize >= BASE_HEADER_BYTES + 8 && buffer.byteLength >= BASE_HEADER_BYTES + 8)
    {
        hasWaveform = BigInt(0) !== view.getBigUint64(227, true);
    }

    if (headerSize >= LAS_HEADER_BYTES && buffer.byteLength >= LAS_HEADER_BYTES)
    {
        numEVLR = view.getUint32(243, true);
        const numPoints64 = view.getBigUint64(247, true);

        if (numPoints64 > BigInt(0))
        {
            numPoints = numPoints64;
        }
    }

    const lines = [text, '', '--- size check ---'];

    lines.push('point data offset (header + VLRs): ' + pointDataOffset + ' bytes');
    lines.push('points: ' + numPoints.toString() + ' x ' + recordLen + ' bytes = ' + (numPoints * BigInt(recordLen)).toString() + ' bytes');

    let ok = false;

    if (hasWaveform || numEVLR > 0)
    {
        const reason = hasWaveform ? 'the file carries waveform data packets' : 'the file carries ' + numEVLR + ' extended VLR(s) after the point data';

        lines.push('file size cannot be verified: ' + reason + ', whose size the header does not state');
        lines.push('actual file size: ' + actualSize + ' bytes');
    }
    else
    {
        const expected = BigInt(pointDataOffset) + numPoints * BigInt(recordLen);

        lines.push('expected file size: ' + expected.toString() + ' bytes');
        lines.push('actual file size: ' + actualSize + ' bytes');

        ok = expected === BigInt(actualSize);
        lines.push(ok ? 'MATCH' : 'MISMATCH (' + (BigInt(actualSize) - expected).toString() + ' bytes difference)');
    }

    return { text: lines.join('\n'), ok: ok };
};

//Fetches just the header bytes of a recording's las file (HTTP range request; the servers
//serve maps/ with Accept-Ranges), reads the exact file size from the 206 response's
//Content-Range total, and formats header + size check. Always resolves to { text, ok } --
//failures come back as an explanatory message with a failing status.
export const loadLasHeader = (recording, filePath) => {

    return fetch('maps/' + recording + '/' + filePath, {
        cache: 'no-store',
        headers: { 'Range': 'bytes=0-' + (LAS_HEADER_BYTES - 1) }
    })
    .then((res) => {
        if (!res.ok)
        {
            throw new Error('could not fetch the file (HTTP ' + res.status + ')');
        }

        //Content-Range: bytes 0-374/123456789 -- the total after the slash is the size
        const contentRange = res.headers.get('content-range') || '';
        const slash = contentRange.lastIndexOf('/');
        const total = slash >= 0 ? parseInt(contentRange.substring(slash + 1), 10) : NaN;

        if (206 !== res.status || isNaN(total))
        {
            throw new Error('server did not answer the range request with the file size');
        }

        return res.arrayBuffer().then(buffer => buildLasReport(buffer, total));
    })
    .catch(err => ({ text: 'Could not read LAS header: ' + err.message, ok: false }));
};
